import clsx from 'clsx';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import {
  getDateFnsLocale,
  parseDateKey,
  shiftDateKey,
  sortPlansChronologically,
  toDateKey,
  todayKey,
} from '@/domain/dates';
import { filterPlans, resolveDayCellFillColor, dayCellSurfaceStyle, canMutatePlan } from '@/domain/plans';
import { loadDayMediaPreviews, type DayMediaPreview } from '@/platform/dayMedia';
import { useLayoutMode } from '@/platform/useLayoutMode';
import { DayMediaThumb } from '@/features/plans/DayMediaThumb';
import { PlanChip } from '@/features/plans/PlanChip';
import { toastStore } from '@/components/ui/Toast';
import styles from './CalendarGrid.module.css';

export function CalendarGrid() {
  const { t, i18n } = useTranslation();
  const layoutMode = useLayoutMode();
  const isMobile = layoutMode === 'mobile';
  const maxChips = isMobile ? 2 : 3;
  const maxThumbs = isMobile ? 1 : 3;

  const session = useAppStore((s) => s.session);
  const plans = useAppStore((s) => s.plans);
  const profiles = useAppStore((s) => s.profiles);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);
  const cursorMonth = useAppStore((s) => s.cursorMonth);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const movePlanToDate = useAppStore((s) => s.movePlanToDate);
  const copyPlanToDate = useAppStore((s) => s.copyPlanToDate);
  const space = useAppStore((s) => s.space);
  const spaceId = space?.id;

  const gridRef = useRef<HTMLDivElement>(null);
  const [mediaPreviews, setMediaPreviews] = useState<Record<string, DayMediaPreview>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const locale = preferences?.locale ?? (i18n.language.startsWith('ru') ? 'ru' : 'en');
  const weekStartsOn = preferences?.weekStartsOn ?? 1;
  const viewerId = session?.user.id ?? '';
  const today = todayKey();
  const dateFnsLocale = getDateFnsLocale(locale);

  const monthStart = useMemo(
    () => startOfMonth(parseDateKey(`${cursorMonth}-01`)),
    [cursorMonth],
  );

  const gridDays = useMemo(() => {
    const monthEnd = endOfMonth(monthStart);
    const gridStart = startOfWeek(monthStart, { weekStartsOn });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [monthStart, weekStartsOn]);

  const weeks = useMemo(() => {
    const rows: Date[][] = [];
    for (let i = 0; i < gridDays.length; i += 7) {
      rows.push(gridDays.slice(i, i + 7));
    }
    return rows;
  }, [gridDays]);

  useEffect(() => {
    if (!spaceId) {
      setMediaPreviews({});
      return;
    }
    const keys = gridDays.map((d) => toDateKey(d));
    let cancelled = false;
    void loadDayMediaPreviews(spaceId, keys).then((previews) => {
      if (!cancelled) setMediaPreviews(previews);
    });
    return () => {
      cancelled = true;
    };
  }, [spaceId, gridDays]);

  const weekdayLabels = useMemo(() => {
    const anchor = startOfWeek(new Date(), { weekStartsOn });
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(anchor, i), 'EEE', { locale: dateFnsLocale }),
    );
  }, [weekStartsOn, dateFnsLocale]);

  const visiblePlans = useMemo(() => {
    if (!viewerId) return [];
    return filterPlans(
      plans.filter((p) => !p.deletedAt && p.planDate),
      filters,
      viewerId,
    );
  }, [plans, filters, viewerId]);

  const plansByDate = useMemo(() => {
    const map = new Map<string, typeof visiblePlans>();
    for (const plan of visiblePlans) {
      if (!plan.planDate) continue;
      const list = map.get(plan.planDate) ?? [];
      list.push(plan);
      map.set(plan.planDate, list);
    }
    return map;
  }, [visiblePlans]);

  const focusDate = selectedDate ?? today;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!focusDate) return;

      let next: string | null = null;
      switch (event.key) {
        case 'ArrowLeft':
          next = shiftDateKey(focusDate, -1);
          break;
        case 'ArrowRight':
          next = shiftDateKey(focusDate, 1);
          break;
        case 'ArrowUp':
          next = shiftDateKey(focusDate, -7);
          break;
        case 'ArrowDown':
          next = shiftDateKey(focusDate, 7);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          setSelectedDate(focusDate);
          return;
        case 'Escape':
          if (lightboxUrl) {
            event.preventDefault();
            setLightboxUrl(null);
          }
          return;
        default:
          return;
      }

      event.preventDefault();
      setSelectedDate(next);
    },
    [focusDate, lightboxUrl, setSelectedDate],
  );

  const handleDragStart = (planId: string) => (event: DragEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.dataTransfer.setData('text/plain', planId);
    event.dataTransfer.effectAllowed = 'copyMove';
  };

  const isCopyGesture = (event: { ctrlKey: boolean; metaKey: boolean; altKey: boolean }) =>
    event.ctrlKey || event.metaKey || event.altKey;

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = isCopyGesture(event) ? 'copy' : 'move';
  };

  const handleDrop = (dateKey: string) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const planId = event.dataTransfer.getData('text/plain');
    if (!planId) return;
    if (isCopyGesture(event) || event.dataTransfer.dropEffect === 'copy') {
      void copyPlanToDate(planId, dateKey).then((copy) => {
        if (copy) toastStore.show({ type: 'success', message: t('plan.copied') });
      });
      return;
    }
    void movePlanToDate(planId, dateKey);
  };

  const openMedia = (event: MouseEvent, dateKey: string, url: string | null) => {
    event.stopPropagation();
    setSelectedDate(dateKey);
    if (url) setLightboxUrl(url);
  };

  const currentMonthPrefix = cursorMonth;

  return (
    <>
      <div
        ref={gridRef}
        className={styles.grid}
        tabIndex={0}
        role="grid"
        aria-label={format(monthStart, 'LLLL yyyy', { locale: dateFnsLocale })}
        title={t('plan.copyDragHint')}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.weekdays} role="row">
          {weekdayLabels.map((label) => (
            <div key={label} className={styles.weekday} role="columnheader">
              {label}
            </div>
          ))}
        </div>

        <div className={styles.weeks}>
          {weeks.map((week) => (
            <div key={week[0]?.toISOString()} className={styles.week} role="row">
              {week.map((day) => {
                const dateKey = toDateKey(day);
                const dayPlans = sortPlansChronologically(plansByDate.get(dateKey) ?? []);
                const shown = dayPlans.slice(0, maxChips);
                const extra = dayPlans.length - shown.length;
                const isOutside = !dateKey.startsWith(currentMonthPrefix);
                const isToday = dateKey === today;
                const isSelected = dateKey === selectedDate;
                const fillHex = viewerId
                  ? resolveDayCellFillColor(dayPlans, profiles, viewerId)
                  : null;
                const media = mediaPreviews[dateKey];
                const mediaCount = media?.count ?? 0;
                const sources = media?.sources ?? [];
                const visibleThumbs = sources.slice(0, maxThumbs);
                const hiddenThumbs = Math.max(0, mediaCount - visibleThumbs.length);

                return (
                  <div
                    key={dateKey}
                    role="gridcell"
                    aria-selected={isSelected}
                    className={clsx(
                      styles.cell,
                      fillHex && styles.tinted,
                      isOutside && styles.outside,
                      isToday && styles.today,
                      isSelected && styles.selected,
                      mediaCount > 0 && styles.hasMedia,
                    )}
                    style={fillHex ? dayCellSurfaceStyle(fillHex) : undefined}
                    onClick={() => setSelectedDate(dateKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedDate(dateKey);
                      }
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop(dateKey)}
                    tabIndex={isSelected ? 0 : -1}
                  >
                    <div className={styles.dayHeader}>
                      <span className={styles.dayNum}>{format(day, 'd')}</span>
                      {isToday && <span className={styles.todayLabel}>{t('common.today')}</span>}
                    </div>

                    <div className={styles.plansStack}>
                      {shown.map((plan) => (
                        <PlanChip
                          key={plan.id}
                          plan={plan}
                          grid
                          compact
                          mobileStack={isMobile}
                          compactTime={isMobile}
                          draggable={canMutatePlan(plan, viewerId)}
                          onDragStart={
                            canMutatePlan(plan, viewerId) ? handleDragStart(plan.id) : undefined
                          }
                        />
                      ))}
                      {extra > 0 && (
                        <span className={styles.more}>{t('app.moreCount', { count: extra })}</span>
                      )}
                    </div>

                    <div className={styles.flexibleSpace} aria-hidden />

                    {mediaCount > 0 && (
                      <div
                        className={styles.mediaStrip}
                        aria-label={t('dayMedia.badge')}
                        onClick={(e) =>
                          openMedia(e, dateKey, visibleThumbs[0]?.url ?? null)
                        }
                      >
                        <div className={styles.mediaFade} aria-hidden />
                        <div className={styles.mediaThumbs}>
                          {visibleThumbs.map((source, index) => (
                            <DayMediaThumb
                              key={`${dateKey}-${source.mediaId}-${index}`}
                              source={source}
                              className={clsx(
                                styles.thumbBtn,
                                isMobile && styles.thumbBtnFull,
                                !isMobile &&
                                  index === 0 &&
                                  visibleThumbs.length > 1 &&
                                  styles.thumbPrimary,
                              )}
                              imgClassName={styles.thumb}
                              onClick={(e) => openMedia(e, dateKey, source.url)}
                            />
                          ))}
                          {visibleThumbs.length === 0 && (
                            <span className={styles.thumbSkeleton} aria-hidden />
                          )}
                          {hiddenThumbs > 0 && (
                            <span className={styles.mediaMore}>+{hiddenThumbs}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {lightboxUrl && (
        <button
          type="button"
          className={styles.lightbox}
          onClick={() => setLightboxUrl(null)}
          aria-label={t('common.close')}
        >
          <img src={lightboxUrl} alt="" className={styles.lightboxImg} />
        </button>
      )}
    </>
  );
}
